import { BindingEngine, observable, computedFrom } from "aurelia-binding";
import { autoinject } from "aurelia-dependency-injection";
import { EventAggregator } from "aurelia-event-aggregator";
import { I18N } from "aurelia-i18n";
import { SourceType, DataType, QueryFilter, DataGridColumn } from "../../../../models/queries/queries.models";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { ActiveMenuItemsService } from "../../../../services/basics/active-menu-items.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { ListService } from "../../../../services/modules/list-client.service";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { BaseListItems } from "../../admin/lists/base-list-items";
import * as moment from "moment";
import { MobileHelper } from "../../../../helpers/mobile-helper";
import { Guid } from "../../../../utilities/guid";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { RangeSelectorHelper } from "../../../components/range-selector/range-selector";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import { MultiLookupModel, RightShipMultiLookupModels } from "../../../components/multi-checked-list-selector/multi-checked-list-selector";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { VoyageService } from "../../../../services/voyage/voyage.service";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../models/basics/basics.models";
import { Reflection } from "../../../../utilities/reflection";

@autoinject
export class CarbonAccountingVoyageRecords extends BaseListItems {

    public portEntityTypes: EntityTypeConfigurationModel[];
    public initialFilters: QueryFilter[];
    public isMobileFilterOpen: boolean;
    public calculationTypes: { id: number, name: string }[] = [
        {
            id: 1,
            name: "EVDI"
        }, {
            id: 2,
            name: "Fuel"
        },
    ];

    @observable()
    private fromYear: any;
    private toYear: any;

    constructor(dialogClient: DialogClientService, notificationClientService: NotificationClientService,
        listService: ListService, i18N: I18N, activeMenuItemsService: ActiveMenuItemsService, eventAggregator: EventAggregator,
        customerService: CommonCustomerService, private dialogClientService: DialogClientService, private voyageService: VoyageService,
        userClaimsService: UserClaimsService, validationsClientService: ValidationsClientService, signalService: SignalClientService,
        protected bindingEngine: BindingEngine, private entityTypeConfigurationHelper: EntityTypeConfigurationHelper) {
        super(dialogClient, notificationClientService, listService, i18N, bindingEngine, 'Voyage Records',
            activeMenuItemsService, eventAggregator, customerService, userClaimsService, signalService, validationsClientService);
        this.portEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Ports"]);
        this.setColumns();
    }

    vesselId: number;
    ratings: { name: string, weight: number }[] =
        [
            { name: "A", weight: 80 },
            { name: "B", weight: 70 },
            { name: "C", weight: 60 },
            { name: "D", weight: 50 },
            { name: "E", weight: 40 },
            { name: "F", weight: 30 },
            { name: "G", weight: 20 },
            { name: "U", weight: 10 }
        ];

    activate(model: any) {
        if (model) {
            if (model.customerId)
                this.selectedCustomerId = model.customerId;
            if (model.vesselId)
                this.vesselId = model.vesselId;
            if (model.yearFrom)
                this.fromYear = model.yearFrom;
            if (model.yearTo)
                this.toYear = model.yearTo;
            if (model.ghg && model.ghg.length > 0)
                this.selectedGhgRatingWeights = model.ghg.map(x => {
                    const r = this.ratings.filter(a => a.name.toLowerCase() == x.toLowerCase())[0];
                    return r ? r.weight : 0;
                });
            if (model.vesselTypeGroupId) {
                this.mclFilters[LookupValueQuery.VesselTypes] = [
                    {
                        filterOperator: {
                            translatedSymbol: "{0} in ({1})",
                            friendlyName: "in",
                            requiresFilterValue: true,
                            requiresInterpolation: true
                        },
                        filterProperty: {
                            name: "vesselTypeGroupId",
                            isDynamicProperty: false,
                            type: DataType.Number,
                            isNullable: true,
                        },
                        filterValue: model.vesselTypeGroupId || [],
                        isAnd: true,
                        skipOnNullOrEmpty: true
                    },
                    {
                        filterOperator: {
                            translatedSymbol: "{0} in ({1})",
                            friendlyName: "in",
                            requiresFilterValue: true,
                            requiresInterpolation: true
                        },
                        filterProperty: {
                            name: "shipTypeId",
                            isDynamicProperty: false,
                            type: DataType.Number,
                            isNullable: true,
                        },
                        filterValue: model.vesselTypeId || [],
                        isAnd: true,
                        skipOnNullOrEmpty: true
                    }
                ];
            }
        }
    }

    @computedFrom(Reflection.getPropertyPath<CarbonAccountingVoyageRecords>(z => z.showCargoTypeFilter),
        Reflection.getPropertyPath<CarbonAccountingVoyageRecords>(z => z.showGhgRatingFilter),
        Reflection.getPropertyPath<CarbonAccountingVoyageRecords>(z => z.showOpIndexRangeFilter),
        Reflection.getPropertyPath<CarbonAccountingVoyageRecords>(z => z.vesselTypeFilterActive))
    public get isSubFilterOpen(): boolean {
        return this.showCargoTypeFilter || this.showGhgRatingFilter || this.showOpIndexRangeFilter ||
            this.vesselTypeFilterActive;
    }

    public get mobileFilterSelected() {
        return false;
    }

    protected get odataUrl(): string {
        return "odata/CarbonAccountingVoyageRecords";
    }

    public mobileFilterApply(toggleDialog: boolean = true) {
        if (toggleDialog)
            this.toggleMobileFilterDialog();

        // this.appliedFilter = this.filter;
        // this.selectedStatusFilter = [];
    }

    public mobileApplySelectedFilter() {
        // if (this.selectedFilter) {
        //     this.selectedFilter.apply();
        //     this.selectedFilter = null;
        // }
    }

    attached() {
        this.setItemSource();
    }

    private setItemSource() {
        const currentFilters: QueryFilter[] = [];

        if (this.selectedCustomerId) {
            currentFilters.push(QueryFilter.getQuery('customerId', this.selectedCustomerId, 'eq', DataType.Number, true));
        }

        if (this.vesselId)
            currentFilters.push(QueryFilter.getQuery('vesselId', this.vesselId, "eq", DataType.Number, true));

        if (this.fromYear)
            currentFilters.push(QueryFilter.getQuery('financialYear', this.fromYear, "ge", DataType.Number, true));

        if (this.toYear)
            currentFilters.push(QueryFilter.getQuery('financialYear', this.toYear, "le", DataType.Number, true));

        //#region date range values

        if (this.dateRangeFilterActive) {
            currentFilters.push(QueryFilter.getQuery('voyageDate', this.startDate, 'ge', DataType.Date, true));
            currentFilters.push(QueryFilter.getQuery('voyageDate', this.endDate, 'le', DataType.Date, true));
        }

        //#endregion
        //#region ghg values

        if (this.selectedGhgRatingWeights && this.selectedGhgRatingWeights.length > 0) {

            const ghgRatingFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "ghgRatingWeight",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedGhgRatingWeights || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            currentFilters.push(ghgRatingFilter);
        }

        //#endregion

        //#region opIndexRange values

        if (this.opIndexRangeFirstFilter || this.opIndexRangeSecondFilter) {
            currentFilters.push(QueryFilter.getQuery('opIndexRange', this.opIndexRangeFirstFilter ? this.opIndexRangeFirstFilter : 0, 'ge', DataType.Number, true));
            currentFilters.push(QueryFilter.getQuery('opIndexRange', this.opIndexRangeSecondFilter ? this.opIndexRangeSecondFilter : 0, 'le', DataType.Number, true));
        }

        //#endregion

        if (this.selectedPort && this.selectedPort.length > 0) {
            const portFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "voyageId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.matchedVoyageIds.voyageIds.length != 0 ? this.matchedVoyageIds.voyageIds : 0,
                isAnd: true,
                skipOnNullOrEmpty: true,
            };
            currentFilters.push(portFilter);
        }

        //#region cargo types values

        if (this.cargoTypeFilterActive) {

            const cargoTypeFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "cargoTypeId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedCargoTypeCategories || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            currentFilters.push(cargoTypeFilter);
        }

        //#endregion

        //#region vessel types values

        if (this.vesselTypeFilterActive) {
            this.mclFilters[LookupValueQuery.VesselTypes].forEach(vtf => {
                currentFilters.push(vtf);
            });
        }

        //#endregion

        //#region dwt values

        if (this.dwtFirstFilter || this.dwtSecondFilter) {
            currentFilters.push(QueryFilter.getQuery('dwt', this.dwtFirstFilter ? this.dwtFirstFilter : 0, 'ge', DataType.Number, true));
            currentFilters.push(QueryFilter.getQuery('dwt', this.dwtSecondFilter ? this.dwtSecondFilter : 0, 'le', DataType.Number, true));
        }

        //#endregion

        //#region calculation types values

        if (this.calculationTypeFilterActive) {

            const calculationTypeFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "calculationTypeId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedCalculationTypes || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            currentFilters.push(calculationTypeFilter);
        }

        //#endregion

        //#region search filter

        if (this.searchFilter) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("vesselName", this.searchFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("internationalMaritimeOrganizationNumber", this.searchFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("voyageNumber", this.searchFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
        }

        //#endregion

        this.itemSource = this.getItemSource(currentFilters);
    }

    private getItemSource(filters) {
        this.initialFilters = filters;

        return {
            sourceType: SourceType.ODataUrl,
            source: this.odataUrl,
            columns: this.columns,
            initialFilters: this.initialFilters,
            initialSorting: this.initialSorting
        };
    }

    setColumns() {
        const columns = [];

        columns.push(
            {
                columnOrder: 0,
                sortOrder: -1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'voyageNumber',
                title: 'Voyage ID',
                isSortable: true,
                value: '<div class="column">' +
                    '<span><strong>VOY ${item.voyageNumber}</strong></span>' +
                    '</div>',
                parameter: {
                    item: null
                }
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.CustomTemplate,
                name: 'vesselName',
                title: 'Vessel Name',
                isSortable: false,
                instancePerRow: true,
                value: '<div class="column">' +
                    '<div class="row align-items-middle">' +
                    '<span>${item.vesselName}</span>' +
                    '</div>' +
                    '<span if.bind="item.internationalMaritimeOrganizationNumber">IMO ${item.internationalMaritimeOrganizationNumber}</span></div>' +
                    '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span></div>' +
                    '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span></div>' +
                    '</div>',
                parameter: {
                    item: null
                }
            },
            {
                columnOrder: 0,
                sortOrder: 1,
                dataType: DataType.DateTime,
                name: 'voyageDate',
                title: 'Date',
                isSortable: true,
                valueConverter: (date: Date | string) => date
                    ? moment(date).format('DD MMM YYYY')
                    : ''
            },
            {
                columnOrder: 0,
                sortOrder: 2,
                dataType: DataType.Default,
                name: 'voyageRoute',
                title: 'Voyage',
                isSortable: false
            },
            {
                columnOrder: 0,
                sortOrder: 3,
                dataType: DataType.CustomTemplate,
                name: 'totalCargoQuantity',
                title: 'Cargo Type & Quantity',
                isSortable: true,
                instancePerRow: true,
                value: '<div class="column">' +
                    '<span if.bind="item.cargoTypes">${item.cargoTypes} (${item.totalCargoQuantity ? item.totalCargoQuantity : 0 | numberFormat:0})</span>' +
                    '</div>',
                parameter: {
                    item: null
                }
            },
            {
                dataType: DataType.CustomTemplate,
                value: '<comp-ghg-rating rating.bind="item.ghgRating" rating-plus.bind="item.isGhgRatingPlus" ' +
                    'is-verified.bind="item.isGhgRatingVerified"></comp-ghg-rating>',
                name: "ghgRatingWeight",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "GHG Rating",
                instancePerRow: true,
                sortOrder: 0,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null
                }
            },
            {
                columnOrder: 0,
                sortOrder: 5,
                dataType: DataType.Default,
                name: 'computedUnit',
                title: 'Computed Unit',
                isSortable: true
            },
            {
                columnOrder: 0,
                sortOrder: 5,
                dataType: DataType.Double,
                name: 'aer',
                title: 'AER (gCO' + '2'.sub() + '/t.nm)',
                isSortable: true,
                valueConverter: (number: number) => number ? number.toFixed(1) : '-'
            },

            {
                columnOrder: 0,
                sortOrder: 6,
                dataType: DataType.Double,
                name: 'eeoi',
                title: 'EEOI (gCO' + '2'.sub() + '/t.nm)',
                isSortable: true,
                valueConverter: (number: number) => number ? number.toFixed(1) : '-'
            },
            {
                columnOrder: 0,
                sortOrder: 7,
                dataType: DataType.CustomTemplate,
                name: 'opIndexRange',
                title: 'OpIndex Range',
                isSortable: true,
                instancePerRow: true,
                value: '<div class="row">' +
                    '<comp-icon class="icon-down" icon="arrow-down" if.bind="item.opIndexRange && item.opIndexRange < 0"></comp-icon>' +
                    '<comp-icon class="icon-up" icon="arrow-up" if.bind="item.opIndexRange && item.opIndexRange > 0"></comp-icon>' +
                    '<span if.bind="item.opIndexRange">${item.opIndexRange | numberFormat:1}%</span>' +
                    '<span if.bind="!item.opIndexRange">-</span>' +
                    '</div>',
                parameter: {
                    item: null
                }
            },

            {
                columnOrder: 0,
                sortOrder: 8,
                dataType: DataType.CustomTemplate,
                name: 'emissionsIntensity',
                title: 'Intensity (gCO' + '2'.sub() + '/t.nm)',
                value: '${item.emissionsIntensity | numberFormat:2}',
                isSortable: true,
                instancePerRow: true,
                parameter: {
                    item: null
                }
            },

            {
                columnOrder: 0,
                sortOrder: 9,
                dataType: DataType.CustomTemplate,
                name: 'totalEmissions',
                title: 'Total Emissions (tCO' + '2'.sub() + '/e)',
                isSortable: true,
                instancePerRow: true,
                value: '<div class="row">' +
                    '<span if.bind="item.totalEmissions">${item.totalEmissions | numberFormat:0}</span>' +
                    '<span if.bind="!item.totalEmissions">-</span>' +
                    '<span class="theoretical" if.bind="item.isTheoritical && item.totalEmissions">T</span>' +
                    '</div>',
                parameter: {
                    item: null
                }
            },
            {
                name: "expand",
                title: "",
                dataType: DataType.Expander,
                value: (x, rowItem) => {
                    if (!rowItem.isSubContentExpanded && !rowItem.item.voyageData) {
                        this.voyageService.getVoyageInfo(rowItem.item.voyageId).then(response => {
                            if (response) {
                                rowItem.item.voyageData = response;
                            }
                        }).catch(() => {
                            this.notificationClientService.show(this.i18N.tr('error-text'), "Could not get the Voyage Info", NotificationType.Error);
                        })
                    }
                    rowItem.isSubContentExpanded = !rowItem.isSubContentExpanded;
                },
                parameter: "keyboard_arrow_down",
                secondParameter: "keyboard_arrow_up",
                sortOrder: -1,
                columnOrder: -1,
                includeInExcel: false
            }
        );

        columns.forEach((column, index) => {
            column.columnOrder = index;
            column.sortOrder = index;
        });

        this.columns = columns;
    }

    clearAllFilters(reload: boolean = true) {
        if (this.dataGridVM) {
            this.dataGridVM.clearSorting();
        }
        this.showDwtFilter = false;
        this.selectedCargoTypeCategories = [];
        this.selectedGhgRatingWeights = null;
        this.opIndexRangeFirstFilter = null;
        this.opIndexRangeSecondFilter = null;
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.dwtIsApplied = false;
        this.selectedPort = [];
        this.matchedVoyageIds = [];
        if (reload)
            this.setItemSource();
    }

    public closeAllFilters() {
        this.showCargoTypeFilter = false;
        this.showGhgRatingFilter = false;
        this.showOpIndexRangeFilter = false;
        this.showDwtFilter = false;
        this.showDateRangeFilter = false;
    }

    public selectedFilter: { title: string, clear: () => void, reset: () => void, apply: () => void };

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        // this.showCustomerFilter = false;
        // this.show Filter = false;
    }

    //#region date range filter
    public selectedCustomerId?: number;

    selectedCustomerChanged($event) {
        if (!$event.detail || ($event.detail.oldValue && $event.detail.value === $event.detail.oldValue))
            return;
        this.setItemSource();
    }
    //#endregion

    //#region date range filter
    public startDate: number;
    public endDate: number;
    public showDateRangeFilter: boolean = false;

    public toggleDateRangeFilter() {
        if (!this.showDateRangeFilter) {
            this.closeAllFilters();
        }
        this.showDateRangeFilter = !this.showDateRangeFilter;
        this.selectedFilter = {
            title: "Date Range",
            clear: () => {
                this.clearDateRangeFilter();
            },
            reset: () => {
            },
            apply: () => {
                this.applyDateRangeFilter();
            }
        }
    }

    public applyDateRangeFilter() {
        this.setItemSource();
        this.showDateRangeFilter = false;
    }

    public clearDateRangeFilter() {
        this.startDate = null;
        this.endDate = null;
        this.setItemSource();
    }

    @computedFrom("startDate", "endDate")
    public get dateRangeFilterActive() {
        return this.startDate && this.endDate;
    }
    //#endregion

    //#region ghg rating
    public selectedGhgRatingWeights: number[] = [];
    public mobileGhgRatingWeights: number[] = [];
    public showGhgRatingFilter: boolean = false;

    public toggleGhgRatingFilters() {
        if (!this.showGhgRatingFilter) {
            this.closeAllFilters();
        }
        this.showGhgRatingFilter = !this.showGhgRatingFilter;
        this.selectedFilter = {
            title: "GHG Rating",
            clear: () => {
                this.clearGhgRatingFilter();
            },
            reset: () => {
                this.mobileGhgRatingWeights = CloneHelper.cloneObject(this.selectedGhgRatingWeights);
            },
            apply: () => {
                this.applyGhgRating(this.mobileGhgRatingWeights);
            }
        }
    }

    public applyGhgRating(items: number[]) {
        this.selectedGhgRatingWeights = items;
        this.setItemSource();
        this.toggleGhgRatingFilters();
    }

    public clearGhgRatingFilter() {
        this.selectedGhgRatingWeights = [];
        this.mobileGhgRatingWeights = [];
        this.setItemSource();
    }

    @computedFrom("selectedGhgRatingWeights", "selectedGhgRatingWeights.length")
    public get ghgRatingFilterActive() {
        return this.selectedGhgRatingWeights && this.selectedGhgRatingWeights.length > 0;
    }

    //#endregion
    public matchedVoyageIds: any;
    public selectedPort: number[] = [];
    public mobilePort: number[] = [];
    public showPortFilter: boolean = false;

    public togglePortFilters() {
        if (!this.showPortFilter) {
            this.closeAllFilters();
        }
        this.showPortFilter = !this.showPortFilter;
        this.selectedFilter = {
            title: "Port",
            clear: () => {
                this.clearPortFilter();
            },
            reset: () => {
                this.mobilePort = CloneHelper.cloneObject(this.selectedPort);
            },
            apply: () => {
                this.applyPortFilter(this.mobilePort);
            }
        }
    }

    public async applyPortFilter(items: any) {
        if (items.map(x => x.id).length > 0) {
            await this.voyageService.getVoyageIdsFromPortRoute({
                portIds: items.map(x => x.id),
                customerId: this.selectedCustomerId
            }).then(response =>
                this.matchedVoyageIds = response
            );
        }

        this.selectedPort = items.map(x => x.id);
        this.setItemSource();
        this.togglePortFilters();
    }

    public clearPortFilter() {
        this.selectedPort = [];
        this.matchedVoyageIds = [];
        this.setItemSource();
    }

    @computedFrom("selectedPort", "selectedPort.length")
    public get portFilterActive() {
        return this.selectedPort && this.selectedPort.length > 0;
    }
    //#region opIndex Range filter

    public opIndexRangeFirstFilter: number;
    public opIndexRangeSecondFilter: number;
    public mobileOpIndexRangeFilter: { low: number, high: number };
    public opIndexRangeDefaults: { low: number, high: number } = { low: -10, high: 10 };
    public showOpIndexRangeFilter: boolean = false;
    public opIndexRangeIsApplied: boolean = false;

    public toggleOpIndexRangeFilters() {
        if (!this.showOpIndexRangeFilter) {
            this.closeAllFilters();
        }
        this.showOpIndexRangeFilter = !this.showOpIndexRangeFilter;
        this.selectedFilter = {
            title: "OpIndex Range",
            clear: () => {
                this.clearopIndexRangeFilter();
            },
            reset: () => {
                this.mobileOpIndexRangeFilter = RangeSelectorHelper.resetRangeFilter(this.opIndexRangeFirstFilter, this.opIndexRangeSecondFilter, this.opIndexRangeDefaults);
            },
            apply: () => {
                this.applyOpIndexRangeFilter(this.mobileOpIndexRangeFilter);
            }
        }
    }

    public applyOpIndexRangeFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.opIndexRangeFirstFilter = filterValue.low;
            this.opIndexRangeSecondFilter = filterValue.high;
            this.opIndexRangeIsApplied = true;
        }

        this.setItemSource();
        this.toggleOpIndexRangeFilters();
    }

    public clearopIndexRangeFilter() {
        this.opIndexRangeFirstFilter = null;
        this.opIndexRangeSecondFilter = null;
        this.mobileOpIndexRangeFilter = { low: null, high: null };
        this.opIndexRangeIsApplied = false;
        this.setItemSource();
    }

    @computedFrom("opIndexRangeIsApplied")
    public get opIndexRangeFilterActive() {
        return this.opIndexRangeIsApplied;
    }

    //#endregion

    //#region cargo type filter

    //cargo type
    public showCargoTypeFilter: boolean = false;
    public selectedCargoTypeCategories: number[] = [];

    public toggleCargoTypeFilters() {
        if (!this.showCargoTypeFilter) {
            this.closeAllFilters();
        }
        this.showCargoTypeFilter = !this.showCargoTypeFilter;
        this.selectedFilter = {
            title: "Cargo Type",
            clear: () => {
                this.clearCargoTypeFilter();
            },
            reset: () => {

            },
            apply: () => {
                this.applyCargoTypeFilter(this.selectedCargoTypeCategories.map(cId => {
                    return { id: cId };
                }), false);
            }
        }
    }

    public applyCargoTypeFilter(items: any[], reload: boolean = true) {
        this.selectedCargoTypeCategories = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.showCargoTypeFilter = false;
    }

    public clearCargoTypeFilter() {
        this.selectedCargoTypeCategories = [];
        this.setItemSource();
    }

    @computedFrom("selectedCargoTypeCategories", "selectedCargoTypeCategories.length")
    public get cargoTypeFilterActive() {
        return this.selectedCargoTypeCategories && this.selectedCargoTypeCategories.length > 0;
    }

    //#endregion

    //#region Multi Checked List dialog filters
    public cachedMultipleLookups: { [lookup: string]: MultiLookupModel[] } = {};
    public mclFilters: { [lookup: string]: QueryFilter[] } = {};
    //#region vessel type filter
    public vesselTypeDisplayText: string = this.i18N.tr('vessel-type');
    public vesselTypeDescription: string = "";

    public clearVesselTypeFilter(reload: boolean = true) {
        const lookup = LookupValueQuery.VesselTypes;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1)
            this.mclFilters[lookup] = [];
        this.vesselTypeDescription = "";
        this.vesselTypeDisplayText = this.i18N.tr('vessel-type');
        if (reload)
            this.setItemSource();
    }

    @computedFrom("mclFilters", "mclFilters.RS_VesselTypes", "mclFilters.RS_VesselTypes.length")
    public get vesselTypeFilterActive() {
        const lookup = LookupValueQuery.VesselTypes;
        return (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0);
    }
    //#endregion

    openMCLDialog = (type: string) => {
        let lookup = null;
        if (type == "vessel_types")
            lookup = LookupValueQuery.VesselTypes;
        else if (type == "vessel_statuses")
            lookup = LookupValueQuery.VesselStatuses;
        else {
            console.error("invalid multi filter type")
        }
        const displayText = type == "vessel_types" ? this.i18N.tr('vessel-type') : this.i18N.tr('vessel-status-text');

        this.closeAllFilters();

        const cachedMultiLookups = this.cachedMultipleLookups[lookup] ? this.cachedMultipleLookups[lookup] : RightShipMultiLookupModels.getMultiLookupModel(lookup);

        let filterValues: { [filterProperty: string]: number[] } = null;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0) {
            filterValues = {};
            this.mclFilters[lookup].forEach(x => {
                filterValues[x.filterProperty.name] = x.filterValue;
            });
        }

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.MultiCheckedListDialog, {
            multiLookups: cachedMultiLookups,
            title: displayText,
            selectedIds: filterValues,
            onApply: (multiLookups: MultiLookupModel[]) => {

                this.mclFilters[lookup] = [];

                const result = RightShipMultiLookupModels.getMultiLookupFilters(lookup, displayText, multiLookups);

                this.mclFilters[lookup] = result.filters;

                if (lookup == LookupValueQuery.VesselTypes) {
                    this.vesselTypeDisplayText = result.filterDisplayText;
                    this.vesselTypeDescription = result.filterDescription;
                }

                this.cachedMultipleLookups[lookup] = multiLookups;

                this.setItemSource();
            }
        }, true, false, false, 'right');
    }
    //#endregion

    //#region dwt filter
    public dwtFirstFilter: number;
    public dwtSecondFilter: number;
    public mobileDwtFilter: { low: number, high: number };
    public dwtDefaults: { low: number, high: number } = { low: 0, high: 565000 };
    public showDwtFilter: boolean = false;
    public dwtIsApplied: boolean = false;

    public toggleDwtFilters() {
        if (!this.showDwtFilter) {
            this.closeAllFilters();
        }
        this.showDwtFilter = !this.showDwtFilter;
        this.selectedFilter = {
            title: "DWT",
            clear: () => {
                this.clearDwtFilter();
            },
            reset: () => {
                this.mobileDwtFilter = RangeSelectorHelper.resetRangeFilter(this.dwtFirstFilter, this.dwtSecondFilter, this.dwtDefaults);
            },
            apply: () => {
                this.applyDwtFilter(this.mobileDwtFilter);
            }
        }
    }

    public applyDwtFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.dwtFirstFilter = filterValue.low;
            this.dwtSecondFilter = filterValue.high;
            this.dwtIsApplied = true;
        }

        this.setItemSource();
        this.toggleDwtFilters();
    }

    public clearDwtFilter() {
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.mobileDwtFilter = { low: null, high: null };
        this.dwtIsApplied = false;
        this.setItemSource();
    }

    @computedFrom("dwtIsApplied", "dwtFirstFilter", "dwtSecondFilter")
    public get dwtFilterActive() {
        return this.dwtIsApplied && ((this.dwtFirstFilter && this.dwtFirstFilter >= this.dwtDefaults.low) || (this.dwtSecondFilter && this.dwtSecondFilter <= this.dwtDefaults.high));
    }

    //#endregion

    //#region calculation type filter

    //calculation type
    public showCalculationTypeFilter: boolean = false;
    public selectedCalculationTypes: number[] = [];

    public toggleCalculationTypeFilters() {
        if (!this.showCalculationTypeFilter) {
            this.closeAllFilters();
        }
        this.showCalculationTypeFilter = !this.showCalculationTypeFilter;
        this.selectedFilter = {
            title: "Calculation Type",
            clear: () => {
                this.clearCalculationTypeFilter();
            },
            reset: () => {

            },
            apply: () => {
                this.applyCalculationTypeFilter(this.selectedCalculationTypes.map(cId => {
                    return { id: cId };
                }), false);
            }
        }
    }

    public applyCalculationTypeFilter(items: any[], reload: boolean = true) {
        this.selectedCalculationTypes = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleCalculationTypeFilters();
    }

    public clearCalculationTypeFilter() {
        this.selectedCalculationTypes = [];
        this.setItemSource();
    }

    @computedFrom("selectedCalculationTypes", "selectedCalculationTypes.length")
    public get calculationTypeFilterActive() {
        return this.selectedCalculationTypes && this.selectedCalculationTypes.length > 0;
    }

    //#endregion

    //#region Search

    @observable()
    public searchFilter: string = null;

    searchKeyDown($event) {
        if ($event && $event.detail) {
            const code = $event.detail.keyCode;
            if (code === 13) {
                this.setItemSource();
            }
        }
    }

    clearSearchFilter() {
        this.searchFilter = null;
    }

    //#endregion

    downloadExcel() {
        const columns = [<DataGridColumn>
            {
                dataType: DataType.Number,
                name: "voyageNumber",
                title: "Voyage Number",
                columnOrder: 1,
                isHidden: false
            }, <DataGridColumn>
        {
            dataType: DataType.Default,
            name: "vesselName",
            title: "Vessel Name",
            columnOrder: 1,
            isHidden: false
        }, <DataGridColumn>
        {
            dataType: DataType.Default,
            name: "internationalMaritimeOrganizationNumber",
            title: "IMO",
            columnOrder: 1,
            isHidden: false
        }, <DataGridColumn>
        {
            dataType: DataType.Default,
            name: "vehicleIdentificationNumber",
            title: "VIN",
            columnOrder: 1,
            isHidden: false
        }, <DataGridColumn>
        {
            dataType: DataType.Default,
            name: "europeanNumberOfIdentification",
            title: "ENI",
            columnOrder: 1,
            isHidden: false
        }, <DataGridColumn>
        {
            dataType: DataType.Date,
            name: "voyageDate",
            title: "Date",
            columnOrder: 1,
            isHidden: false
        }, <DataGridColumn>
        {
            dataType: DataType.Default,
            name: "voyageRoute",
            title: "Voyage",
            columnOrder: 1,
            isHidden: false
        }, <DataGridColumn>
        {
            dataType: DataType.Default,
            name: "cargoType",
            title: "Cargo Type",
            columnOrder: 1,
            isHidden: false
        }, <DataGridColumn>
        {
            dataType: DataType.Number,
            name: "cargoQuantity",
            title: "Cargo Quantity",
            columnOrder: 1,
            isHidden: false
        }, <DataGridColumn>
        {
            dataType: DataType.Double,
            name: 'aer',
            title: 'AER (gCO2/t.nm)',
            columnOrder: 1,
            isHidden: false
        }, <DataGridColumn>
        {
            dataType: DataType.Double,
            name: 'eeoi',
            title: 'EEOI (gCO2/t.nm)',
            columnOrder: 1,
            isHidden: false
        }, <DataGridColumn>
        {
            dataType: DataType.Double,
            name: 'opIndexRange',
            title: 'OpIndex Range',
            columnOrder: 1,
            isHidden: false
        }, <DataGridColumn>
        {
            dataType: DataType.Double,
            name: "emissionsIntensity",
            title: "Intensity",
            columnOrder: 1,
            isHidden: false
        }, <DataGridColumn>
        {
            dataType: DataType.Double,
            name: "totalEmissions",
            title: "Total Emissions",
            columnOrder: 1,
            isHidden: false
        }]

        this.dataGridVM.downloadExcel(columns);
    }

    async showDeclarationDialog(voyageId: number) {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.DeclarationDialog, {
            voyageId: voyageId
        }, true, false, false, "right");
    }
}
